/**
 * Typed websocket client for the Vainglory Claude sidecar.
 *
 * The sidecar (claude-sidecar/ in the Rust backend repo) hosts one headless
 * Claude Code session per connection, bound to a repo working directory. This
 * client speaks its JSON protocol; see claude-sidecar/README.md.
 */

export const SIDECAR_URL =
  process.env.REACT_APP_SIDECAR_URL ?? "ws://localhost:8787";

// ---- Server -> client events -------------------------------------------------

export interface SidecarInit {
  type: "system";
  subtype: "init";
  sessionId: string;
  model: string;
  cwd: string;
  tools: string[];
  apiKeySource: string;
  permissionMode: string;
}

export interface SidecarAssistant {
  type: "assistant";
  text: string;
}

export interface SidecarToolUse {
  type: "tool_use";
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface SidecarPermissionRequest {
  type: "permission_request";
  id: string;
  name: string;
  input: Record<string, unknown>;
  suggestions: unknown[];
  blockedPath: string | null;
}

export interface SidecarResult {
  type: "result";
  subtype: string;
  isError: boolean;
  result?: string;
  costUsd: number;
  numTurns: number;
  sessionId: string;
}

export type SidecarEvent =
  | { type: "ready" }
  | SidecarInit
  | SidecarAssistant
  | SidecarToolUse
  | SidecarPermissionRequest
  | SidecarResult
  | { type: "stderr"; data: string }
  | { type: "error"; message: string }
  | { type: "closed" };

// ---- Client -> server messages ----------------------------------------------

export interface StartOptions {
  /** Optional: when connecting through the Rocket proxy it injects the path. */
  repoPath?: string;
  prompt?: string;
  allowedTools?: string[];
  permissionMode?: "default" | "acceptEdits" | "plan" | "bypassPermissions";
  appendSystemPrompt?: string;
  model?: string;
  resume?: string;
}

export type ConnectionStatus = "connecting" | "open" | "closed" | "error";

export class ClaudeSidecarClient {
  private ws: WebSocket | null = null;
  private url: string;
  private onEvent: (e: SidecarEvent) => void;
  private onStatus: (s: ConnectionStatus) => void;

  constructor(
    onEvent: (e: SidecarEvent) => void,
    onStatus: (s: ConnectionStatus) => void = () => {},
    url: string = SIDECAR_URL
  ) {
    this.onEvent = onEvent;
    this.onStatus = onStatus;
    this.url = url;
  }

  connect() {
    this.onStatus("connecting");
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => this.onStatus("open");
    ws.onclose = () => this.onStatus("closed");
    ws.onerror = () => this.onStatus("error");
    ws.onmessage = (ev) => {
      try {
        this.onEvent(JSON.parse(ev.data) as SidecarEvent);
      } catch {
        this.onEvent({ type: "error", message: "Bad message from sidecar." });
      }
    };
  }

  private send(obj: Record<string, unknown>) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  start(opts: StartOptions) {
    this.send({ type: "start", ...opts });
  }

  input(text: string) {
    this.send({ type: "input", text });
  }

  respondPermission(
    id: string,
    behavior: "allow" | "deny",
    extra: { updatedInput?: Record<string, unknown>; message?: string } = {}
  ) {
    this.send({ type: "permission", id, behavior, ...extra });
  }

  interrupt() {
    this.send({ type: "interrupt" });
  }

  end() {
    this.send({ type: "end" });
  }

  close() {
    this.end();
    this.ws?.close();
    this.ws = null;
  }
}
